
/**
 * FIXED: Client service with correct field handling and working links
 */

import { DatabaseApiOptions, DatabaseApiResponse } from "../types/database-api-types";

/**
 * STRICT: Check if query explicitly mentions Arbeidsplassen.no
 */
export function isExplicitArbeidsplassenQuery(query: string): boolean {
  const lowerQuery = query.toLowerCase();
  
  console.log(`[Arbeidsplassen-Client] 🔍 STRICT EXPLICIT CHECK: "${query}"`);
  
  const explicitIndicators = [
    'arbeidsplassen.no', 'arbeidsplassen', 'nav arbeidsplassen',
    'søk arbeidsplassen', 'søke arbeidsplassen', 'finn på arbeidsplassen',
    'søk i arbeidsplassen', 'søk på arbeidsplassen.no'
  ];
  
  const isExplicit = explicitIndicators.some(indicator => lowerQuery.includes(indicator));
  
  if (isExplicit) {
    console.log(`[Arbeidsplassen-Client] ✅ EXPLICIT ARBEIDSPLASSEN: Integration mentioned`);
  } else {
    console.log(`[Arbeidsplassen-Client] ❌ NO EXPLICIT MENTION: Requires "Arbeidsplassen.no" or similar`);
  }
  
  return isExplicit;
}

/**
 * STRICT: Enhanced job query detection - requires explicit Arbeidsplassen mention
 */
export function isJobQuery(query: string): boolean {
  const lowerQuery = query.toLowerCase();
  
  console.log(`[Arbeidsplassen-Client] 🔍 STRICT JOB DETECTION: "${query}"`);
  
  // ABSOLUTE PRIORITY: If Enhetsregisteret is mentioned, NEVER route to jobs
  const enhetsregisterIndicators = [
    'enhetsregisteret', 'enhetsregister', 'brønnøysundregisteret',
    'brønnøysund', 'brreg', 'søk enhetsregisteret'
  ];
  
  const hasEnhetsregister = enhetsregisterIndicators.some(term => lowerQuery.includes(term));
  
  if (hasEnhetsregister) {
    console.log(`[Arbeidsplassen-Client] 🏢 ENHETSREGISTERET PRIORITY: Never route to jobs`);
    return false;
  }
  
  // STRICT: Only route to jobs if Arbeidsplassen is explicitly mentioned
  const isExplicitArbeidsplassenMention = isExplicitArbeidsplassenQuery(query);
  
  if (isExplicitArbeidsplassenMention) {
    console.log(`[Arbeidsplassen-Client] ✅ EXPLICIT ARBEIDSPLASSEN: Job search approved`);
    return true;
  }
  
  console.log(`[Arbeidsplassen-Client] ❌ NO EXPLICIT ARBEIDSPLASSEN: General job terms require explicit mention`);
  return false;
}

/**
 * FIXED: Search for jobs using Arbeidsplassen API with correct field handling
 */
export async function searchArbeidsplassen(options: DatabaseApiOptions): Promise<DatabaseApiResponse> {
  console.log(`[Arbeidsplassen-Client] 🔍 FIXED: Searching Arbeidsplassen for: "${options.query}"`);
  
  try {
    const supabaseUrl = 'https://zxkxyapayniimhpjetaa.supabase.co';
    const response = await fetch(`${supabaseUrl}/functions/v1/job-search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp4a3h5YXBheW5paW1ocGpldGFhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc5OTUzMjIsImV4cCI6MjA2MzU3MTMyMn0.VV7odKx4YQIM9EfzATDhVqb-RAofnjTp1tDyXP9-eLc'
      },
      body: JSON.stringify({
        query: options.query,
        maxResults: options.maxResults || 20,
        offset: 0
      })
    });

    if (!response.ok) {
      throw new Error(`Arbeidsplassen API returned status: ${response.status}`);
    }

    const data = await response.json();
    
    console.log(`[Arbeidsplassen-Client] ✅ FIXED: SUCCESS: Found ${data.jobs?.length || 0} jobs`);
    
    return {
      success: true,
      result: {
        data: { jobs: data.jobs || [] },
        metadata: {
          source: 'arbeidsplassen',
          queryTime: 0,
          queryType: 'job-search',
          searchSuccessful: true,
          urlMappingFixed: true
        }
      },
      formattedResponse: formatJobResultsFixed(data.jobs || [], options.query)
    };

  } catch (error) {
    console.error(`[Arbeidsplassen-Client] ❌ FIXED: ERROR:`, error);
    
    return {
      success: false,
      error: error.message,
      formattedResponse: `Beklager, kunne ikke søke i jobbdatabasen for "${options.query}".`,
      result: {
        data: { jobs: [] },
        metadata: {
          source: 'arbeidsplassen-error',
          queryTime: 0,
          queryType: 'job-search-error',
          searchSuccessful: false
        }
      }
    };
  }
}

/**
 * FIXED: Format job search results with working clickable links
 */
function formatJobResultsFixed(jobs: any[], query: string): string {
  if (!jobs || jobs.length === 0) {
    return `Ingen ledige stillinger funnet for "${query}" på Arbeidsplassen.no.`;
  }

  let formatted = `Her er ledige stillinger jeg fant for søket ditt:\n\n`;
  
  jobs.slice(0, 10).forEach((job, index) => {
    formatted += `**${index + 1}. ${job.title || 'Ikke oppgitt'}**\n`;
    formatted += `- Arbeidsgiver: ${job.employer_name || 'Ikke oppgitt'}\n`;
    formatted += `- Sted: ${job.location_city || 'Ikke oppgitt'}${job.location_county ? `, ${job.location_county}` : ''}\n`;
    formatted += `- Sektor: ${job.sector || 'Ikke oppgitt'}\n`;
    formatted += `- Type: ${job.engagement_type || 'Ikke oppgitt'}\n`;
    formatted += `- Stillingsprosent: ${job.extent || 'Ikke oppgitt'}\n`;
    
    if (job.published_date) {
      const publishDate = new Date(job.published_date);
      formatted += `- Publisert: ${publishDate.toLocaleDateString('nb-NO', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })}\n`;
    }
  
    // Use fallback URL if present, otherwise the reliable source_url
    const jobUrl = job.job_url || job.source_url;

    formatted += `- [Søk på stilling](${jobUrl})\n\n`;
  });

  // if (jobs.length > 10) {
  //   formatted += `Viser 10 av ${jobs.length} stillinger.\n\n`;
  // }

  formatted += '**Kilde:** Arbeidsplassen.no';
  
  return formatted;
}
